/**
 * QA-394 — 관리자가 정하는 회원 비밀번호. 규칙은 BE 회원 비밀번호 정책(user PasswordPolicy)과 같다:
 * 10자 이상 · 영문(대·소문자 합쳐 1종) · 숫자 · 특수문자(영문·숫자가 아닌 모든 문자) 3종 모두. 최대 128자.
 * 화면 1차 검사용이고, 최종 판정은 BE(400 USER_PASSWORD_POLICY_FAILED).
 */

export const MEMBER_PASSWORD_MIN = 10;
export const MEMBER_PASSWORD_MAX = 128;

export type MemberPasswordProblem = 'tooShort' | 'tooLong' | 'needLetter' | 'needDigit' | 'needSpecial';

export const memberPasswordProblems = (pw: string): MemberPasswordProblem[] => {
  const out: MemberPasswordProblem[] = [];
  if (pw.length < MEMBER_PASSWORD_MIN) out.push('tooShort');
  if (pw.length > MEMBER_PASSWORD_MAX) out.push('tooLong');
  if (!/[A-Za-z]/.test(pw)) out.push('needLetter');
  if (!/[0-9]/.test(pw)) out.push('needDigit');
  if (!/[^A-Za-z0-9]/.test(pw)) out.push('needSpecial');
  return out;
};

// 생성용 문자 — 고객이 전화로 받아 적기 쉽게 헷갈리는 글자(0/O/o, 1/l/I)는 뺐다. 특수문자는 고도몰 허용 목록 안에서 고름.
const LOWER = 'abcdefghijkmnpqrstuvwxyz';
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const DIGIT = '23456789';
const SPECIAL = '!@#$%^&*';
const ALL = LOWER + UPPER + DIGIT + SPECIAL;

type RandomFill = (buf: Uint32Array) => Uint32Array;
const cryptoFill: RandomFill = (buf) => crypto.getRandomValues(buf);

/** 암호학적 난수로 규칙을 만족하는 비밀번호를 만든다(소문자·대문자·숫자·특수문자 각 1자 이상). */
export const generateMemberPassword = (length = 12, fill: RandomFill = cryptoFill): string => {
  const len = Math.max(length, MEMBER_PASSWORD_MIN);
  const rand = fill(new Uint32Array(len * 2));
  let r = 0;
  const pick = (set: string) => set[rand[r++]! % set.length]!;
  const chars = [pick(LOWER), pick(UPPER), pick(DIGIT), pick(SPECIAL)];
  while (chars.length < len) chars.push(pick(ALL));
  // 필수 4자가 앞에 몰리지 않게 섞는다(Fisher–Yates).
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rand[r++]! % (i + 1);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.join('');
};
